import olMap from 'ol/Map';
import Layer from "ol/layer/Layer";
import ImageWMS from 'ol/source/ImageWMS';
import ImageLayer from 'ol/layer/Image';
import { AppStorage } from "../AppStorage";
import { MapViewStore } from "../MapViewStore";
import { MapLoadingIndicator } from "./MapLoadingIndicator";

/**
 * Owns the WMS overlay layers (UCR/BOAT/legacy/land registry/etc), their
 * checkbox bindings, legend graphics, the "hide routes of no interest" toggle
 * and edit-mode visibility snapshots.
 */
export class OverlayController {

    private _overlays: Map<string, Layer>;
    private _hideRoutesOfNoInterest: number = 0;

    public constructor(
        private _map: olMap,
        private _wmsUrl: string,
        private _mapStore: MapViewStore,
        private _storage: AppStorage,
        private _loadingIndicator: MapLoadingIndicator,
        private _infoClickProw: number,
        private _showHidden: number,
        private _onLandRegistryHidden: () => void
    ) {
        this.initRoutesOfNoInterest();
        this.addOverlays(this._map);
    }

    public ensureLayerVisibleByName = (layerName: string): void => {
        const selector = this.getCheckboxSelectorForLayer(layerName);
        this.ensureLayerVisibleByCheckbox(selector);
    }

    /**
     * Turns the named overlays on through the normal (persisted) checkbox path so
     * the panel stays in sync and the change survives. Idempotent — a layer that
     * is already visible is left untouched. Used to force the route-context layers
     * (unclassified roads + byways) on when entering GPX edit mode.
     */
    public ensureVisible = (layerNames: string[]): void => {
        layerNames.forEach(name => this.ensureLayerVisibleByName(name));
    }

    private initRoutesOfNoInterest = (): void => {
        let self = this;
        //check storage first
        self._hideRoutesOfNoInterest = parseInt(self._storage.getValue("HideRoutesOfNoInterest")) || 0;

        if (self._hideRoutesOfNoInterest === 1) {
            $("#chkHideRoutesOfNoInterest").prop("checked", true);
        }

        $("#chkHideRoutesOfNoInterest").on("change", function () {
            self._hideRoutesOfNoInterest = ($(this).prop("checked")) ? 1 : 0;
            self._storage.setValue("HideRoutesOfNoInterest", self._hideRoutesOfNoInterest.toString());

            //redraw layers
            if (self._overlays.get("UCRLines").getVisible()) {
                (<ImageWMS>self._overlays.get("UCRLines").getSource()).updateParams({
                    "time": Date.now(),
                    "HIDE_ROUTES_OF_NO_INTEREST": self._hideRoutesOfNoInterest
                });
            }
            if (self._overlays.get("BOATLines").getVisible()) {
                (<ImageWMS>self._overlays.get("BOATLines").getSource()).updateParams({
                    "time": Date.now(),
                    "HIDE_ROUTES_OF_NO_INTEREST": self._hideRoutesOfNoInterest
                });
            }
        });
    }

    private addOverlays = (map: olMap): void => {
        let self = this;
        self._overlays = new Map<string, Layer>();

        let attribution = "This information is subject to Crown copyright and database rights 2021 and is reproduced with the permission of HM Land Registry.</br>The polygons (including the associated geometry, namely x, y co-ordinates) are subject to Crown copyright and database rights 2021 Ordnance Survey 100026316.";
        self.addOverlay(map, "LandRegistry", true);
        self._overlays.get("LandRegistry").getSource().setAttributions(attribution);

        self.addOverlay(map, "HighwaysAuthorities", false);
        self.addOverlay(map, "RepAreas", false);
        self.addOverlay(map, "LEGACYLines", true);
        self.addOverlay(map, "UCRLines", true, true);
        self.addOverlay(map, "BOATLines", true, true);
        self.addOverlay(map, "FavLanes", true, true);

        if (self._infoClickProw) {
            self.addOverlay(map, "PROW-Byway", true, false);
        }

        if (self._showHidden) {
            self.addOverlay(map, "HiddenRoutes", true, false);
        }

        self.addOverlay(map, "RoutesWithoutComments", true, true);
        self.addOverlay(map, "OpenUSRN", true);
        self._overlays.get("OpenUSRN").getSource().setAttributions("Contains OS data © Crown Copyright and database rights " + new Date().getFullYear());

        //set inital map layers visibility
        $("input.mapLayer").each(function () {
            let ck = $(this);
            let layerName = <string>ck.val();
            let defaultChecked = (ck.data("checked") == true);
            let isVisible = self._mapStore.isLayerVisible(layerName, defaultChecked);

            if (isVisible) {
                ck.prop("checked", isVisible);
                self._overlays.get(layerName).setVisible(true);
            }
        });

        //now add the change handlers
        $("input.mapLayer").each(function () {
            let ck = $(this);
            ck.on("change", function () {
                let checked = $(this).is(":checked");
                let layerName = <string>$(this).val();
                self._overlays.get(layerName).setVisible(checked);
                self._mapStore.onLayerVisibiltyChanged(layerName, checked);

                if (layerName === "BOATLines" && !checked) {
                    (<ImageWMS>self._overlays.get("BOATLines").getSource()).updateParams({
                        "time": Date.now(),
                        "HIDE_ROUTES_OF_NO_INTEREST": self._hideRoutesOfNoInterest
                    });
                }

                // Close popup and clear highlights when Land Registry layer is turned off
                if (layerName === "LandRegistry" && !checked) {
                    self._onLandRegistryHidden();
                }

                if (self._infoClickProw) {
                    (<ImageWMS>self._overlays.get("PROW-Byway").getSource()).updateParams({
                        "time": Date.now(),
                        "SHOW_BOAT_PROW": self._infoClickProw
                    });
                }
            });
        });
    }

    private addOverlay = (
        map: olMap,
        layerName: string,
        queryable: boolean,
        useHideRoutesOfNoInterest: boolean = false
    ): void => {
        let layer = this.getWmsImageLayer(layerName, queryable, useHideRoutesOfNoInterest);
        layer.setVisible(false);
        this._overlays.set(layerName, layer);
        map.addLayer(this._overlays.get(layerName));

        let resolution = map.getView().getResolution();
        this.updateLegendGraphic(layer.getSource(), layerName, resolution);
    }

    private getWmsImageLayer = (
        layerName: string,
        queryable: boolean,
        useHideRoutesOfNoInterest: boolean
    ): Layer => {
        let self = this;
        let params = {
            LAYERS: layerName,
            VERSION: "1.3.0"
        };

        if (layerName === "PROW-Byway") {
            params["SHOW_BOAT_PROW"] = self._infoClickProw;
        }

        if (useHideRoutesOfNoInterest) {
            params["HIDE_ROUTES_OF_NO_INTEREST"] = self._hideRoutesOfNoInterest;
        }

        let source = new ImageWMS({
            projection: self._map.getView().getProjection(),
            url: self._wmsUrl,
            params: params,
            serverType: "mapserver",
            ratio: 1.5
        });

        source.on('imageloadstart', function () {
            self._loadingIndicator.onLayerLoading();
        });
        source.on('imageloadend', function () {
            self._loadingIndicator.onLayerLoaded();
        });
        source.on('imageloaderror', function () {
            self._loadingIndicator.onLayerLoaded();
        });

        let lyr = new ImageLayer({
            source: source
        });

        lyr.set("queryable", queryable, true);

        return lyr
    }

    private updateLegendGraphic = (wmsSource, layerName: string, resolution: number): void => {
        let img = <HTMLImageElement>document.getElementById('legend_' + layerName);
        if (img) {
            let graphicUrl = wmsSource.getUrl(resolution) + "?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetLegendGraphic&FORMAT=image%2Fpng&LAYER=" + layerName;
            img.src = graphicUrl;
        }
    }

    private ensureLayerVisibleByCheckbox = (selector: string): void => {
        if (!selector) {
            return;
        }

        let layerCheckbox = $(selector);
        if (layerCheckbox.length && !layerCheckbox.is(":checked")) {
            layerCheckbox.prop("checked", true).trigger("change");
        }
    }

    private getCheckboxSelectorForLayer = (layerName: string): string => {
        if (!layerName) {
            return "";
        }

        const normalized = layerName.toUpperCase();
        const idMap: { [key: string]: string } = {
            "FAVOURITE": "#chkFavLanes",
            "BOAT": "#chkBoatLayer",
            "BOATLINES": "#chkBoatLayer",
            "UCR": "#chkUCRLayer",
            "UCRLINES": "#chkUCRLayer",
            "LEGACY": "#chkLegacyLayer",
            "LEGACYLINES": "#chkLegacyLayer",
            "OPENUSRN": "#chkOpenUsrnLayer"
        };

        if (idMap[normalized]) {
            return idMap[normalized];
        }

        const valueSelector = `input.mapLayer[value='${layerName}']`;
        if ($(valueSelector).length) {
            return valueSelector;
        }

        const normalizedValueSelector = `input.mapLayer[value='${normalized}']`;
        if ($(normalizedValueSelector).length) {
            return normalizedValueSelector;
        }

        return "";
    }
}
